/** Shared queries and mutations (TanStack Query) over the typed client. */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, unwrap, type S } from "./client";

export type SettingsKind = S["SettingsKind"];
export type SettingsDocuments = S["SettingsDocuments"];

export function useOrganization(orgId: string) {
  return useQuery({
    queryKey: ["org", orgId],
    enabled: !!orgId,
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}", { params: { path: { org_id: orgId } } })),
  });
}

/** Whether "Financing & suppliers" is available to the company: off by default, switched on per company by Solar OS
 * (admin console). Undefined while loading. */
export function usePartnersEnabled(orgId: string | null | undefined): boolean | undefined {
  const org = useQuery({
    queryKey: ["org", orgId],
    enabled: !!orgId,
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}", { params: { path: { org_id: orgId! } } })),
  });
  return org.data?.partners_enabled;
}

/** The Lux wallet (Lux & billing, the sidebar's balance). */
export function useLuxWallet(orgId: string) {
  return useQuery({
    queryKey: ["lux", orgId],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/lux", { params: { path: { org_id: orgId } } })),
  });
}

/** Every Lux lot (Lux & billing › Details): when and how it was acquired, what is left and when it expires. */
export function useLuxLots(orgId: string) {
  return useQuery({
    queryKey: ["lux", orgId, "lots"],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/lux/lots", { params: { path: { org_id: orgId } } })),
  });
}

/** The Lux activity log, newest first, a page at a time (optionally one action). */
export function useLuxActivity(orgId: string, action = "") {
  return useInfiniteQuery({
    queryKey: ["lux-activity", orgId, action],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      unwrap(
        api.GET("/v1/orgs/{org_id}/lux/activity", {
          params: { path: { org_id: orgId }, query: { limit: 25, ...(pageParam ? { cursor: pageParam } : {}), ...(action ? { action } : {}) } },
        }),
      ),
    getNextPageParam: (last) => last.next_cursor ?? null,
  });
}

export function useEmbedKeys(orgId: string) {
  return useQuery({
    queryKey: ["embed-keys", orgId],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/embed-keys", { params: { path: { org_id: orgId } } })),
  });
}

export function useEffectiveSettings(orgId: string) {
  return useQuery({
    queryKey: ["settings", orgId],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/settings", { params: { path: { org_id: orgId } } })),
  });
}

/** Saves a settings kind as the organisation's default (the full document as the overlay). `baseVersion` is the
 * version the page's draft was loaded from (`EffectiveSettings.versions[kind]`): when someone else saved since, the
 * API refuses with 409 `settings_changed` and nothing is stored (see `isSettingsConflict`). */
export function useSaveSettings(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, document, baseVersion }: { kind: SettingsKind; document: object; baseVersion?: number }) =>
      unwrap(
        api.PUT("/v1/orgs/{org_id}/settings/{kind}", {
          params: { path: { org_id: orgId, kind } },
          body: { document: document as Record<string, unknown>, ...(baseVersion != null ? { base_version: baseVersion } : {}) },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings", orgId] }),
  });
}

/** The save was refused because someone else changed the same settings after the page loaded them. */
export function isSettingsConflict(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409 && error.problem.code === "settings_changed";
}

/** Our homeowner texts in the company's main language: what "Write my own text" starts from. */
export function useBrandDefaultTexts(orgId: string) {
  return useQuery({
    queryKey: ["brand-default-texts", orgId],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/brand/default-texts", { params: { path: { org_id: orgId } } })),
    staleTime: 5 * 60_000,
  });
}

export function useCreateEmbedKey(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: S["EmbedKeyCreate"]) =>
      unwrap(api.POST("/v1/orgs/{org_id}/embed-keys", { params: { path: { org_id: orgId } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["embed-keys", orgId] }),
  });
}

export function useUpdateEmbedKey(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: S["EmbedKeyUpdate"] }) =>
      unwrap(api.PATCH("/v1/orgs/{org_id}/embed-keys/{key_id}", { params: { path: { org_id: orgId, key_id: id } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["embed-keys", orgId] }),
  });
}

export function useDeleteEmbedKey(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE("/v1/orgs/{org_id}/embed-keys/{key_id}", { params: { path: { org_id: orgId, key_id: id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["embed-keys", orgId] }),
  });
}

export function useLeads(orgId: string, filters: { q?: string; status?: S["DesignStatus"]; source?: S["LeadSource"] }, limit = 25) {
  return useQuery({
    queryKey: ["leads", orgId, filters, limit],
    queryFn: () =>
      unwrap(
        api.GET("/v1/orgs/{org_id}/leads", {
          params: { path: { org_id: orgId }, query: { ...filters, limit } },
        }),
      ),
    placeholderData: (prev) => prev,
  });
}
